<template>
    <CustomDialog v-model="isVisible" :defaultTitle="integration ? $t(integration.name) : ''" width="800px" noScroll>
        <template #content>
            <div class="ppv2-px-8">
                <div v-if="integration && integration.id === 'google_calendar'">
                    <GoogleCalendarIntegration
                        ref="googleCalendarRef"
                        :integration="integration"
                        @success="onSuccess"
                        @error="onError"
                    />
                </div>
                <!-- Assistant IA / MCP : panneau autonome (URL, jetons, mode d'emploi). Le
                     dialogue est monté `noScroll`, ce contenu porte donc son propre défilement. -->
                <div v-else-if="integration && integration.id === 'mcp'" class="mcp-panel-scroll">
                    <McpIntegration @error="onError" />
                </div>
                <div v-else-if="integration && integration.id === 'mailjet'" class="ppv2-space-y-6">
                    <div v-for="setting in form.settings" :key="setting.key">
                        <CustomInput
                            v-model="setting.value"
                            :label="$t(setting.label)"
                            :placeholder="setting.placeholder.includes('.') ? $t(setting.placeholder) : setting.placeholder"
                            :error-text="errors[setting.key]"
                            :class-container-add="setting.key === 'mailjet_secret_key' ? '!ppv2-mb-0' : ''"
                        />
                        <span
                            v-if="setting.key === 'mailjet_secret_key'"
                            class="ppv2-text-xs ppv2-text-gray-400 ppv2-mt-1 ppv2-block"
                        >
                            {{ $t('configuration.integration.mailjetSecretKeyHelper') }}
                        </span>
                    </div>
                </div>
                <div v-else-if="integration && integration.type === 'outil'" class="ppv2-mt-4">
                    <div v-for="setting in form.settings" :key="setting.key">
                        <BaseTextarea
                            v-if="setting.key === 'script'"
                            v-model="setting.value"
                            :label="setting.is_translated ? setting.label : $t(setting.label)"
                            :use-editor="false"
                            :rows="6"
                            :placeholder="setting.is_translated ? setting.placeholder : $t(setting.placeholder)"
                            :error="errors[setting.key]"
                            :required="false"
                        />
                    </div>
                </div>
                <div v-else-if="integration && integration.settings" class="ppv2-space-y-6">
                    <div v-for="setting in form.settings" :key="setting.key">
                        <CustomInput
                            v-model="setting.value"
                            :label="$t(setting.label)"
                            :placeholder="setting.placeholder.includes('.') ? $t(setting.placeholder) : setting.placeholder"
                            :error-text="errors[setting.key]"
                        />
                    </div>
                </div>

                <div
                    v-else
                    class="ppv2-text-center ppv2-text-gray-500"
                    :style="{ paddingTop: normalizeSize(16, true), paddingBottom: normalizeSize(16, true) }"
                >
                    {{ $t('configuration.integration.noSettings') }}
                </div>
            </div>
        </template>
        <template #actions>
            <div class="ppv2-flex ppv2-flex-col ppv2-items-center ppv2-w-full ppv2-gap-3">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-w-full ppv2-gap-4">
                    <BaseButton
                        v-if="isMailjet && !hasSavedApiKey"
                        variant="primary"
                        @click="handleTestAndSave"
                        :disabled="isLoading || isSendingTest || !canSendTest"
                        :customClass="`!ppv2-h-[45px] !ppv2-min-w-[200px] !ppv2-rounded-xl !ppv2-bg-[#2D246E]`"
                    >
                        {{
                            isLoading
                                ? $t('common.saving')
                                : isSendingTest
                                  ? $t('configuration.integration.testEmailSending')
                                  : $t('configuration.integration.testAndSave')
                        }}
                    </BaseButton>
                    <template v-else>
                        <BaseButton
                            v-if="isMailjet"
                            variant="outline"
                            :disabled="isSendingTest || !canSendTest"
                            :customClass="`!ppv2-h-[45px] !ppv2-min-w-[200px] !ppv2-rounded-xl !ppv2-border-[#7d2dbd] !ppv2-text-[#7d2dbd] hover:!ppv2-bg-[#f3eafa]`"
                            @click="sendTestEmail"
                        >
                            {{ $t('configuration.integration.sendTestEmail') }}
                        </BaseButton>
                        <BaseButton
                            v-if="!isMcp"
                            variant="primary"
                            @click="handleSubmit"
                            :disabled="isLoading"
                            :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px] !ppv2-rounded-xl !ppv2-bg-[#2D246E]`"
                        >
                            {{ isLoading ? $t('common.saving') : $t('common.save') }}
                        </BaseButton>
                    </template>
                </div>
                <div v-if="integration && integration.id === 'mailjet'" class="ppv2-h-6">
                    <span
                        v-if="testStatus === 'testing'"
                        class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-text-sm ppv2-text-gray-500"
                    >
                        <span
                            class="ppv2-w-4 ppv2-h-4 ppv2-border-2 ppv2-border-gray-300 ppv2-border-t-[#7d2dbd] ppv2-rounded-full ppv2-animate-spin"
                        />
                        {{ $t('configuration.integration.testEmailSending') }}
                    </span>
                    <span v-else-if="testStatus === 'success'" class="ppv2-text-sm poppinsMedium ppv2-text-[#1a7f4e]">
                        ✓ {{ $t('configuration.integration.testEmailSuccess') }}
                    </span>
                    <span v-else-if="testStatus === 'error'" class="ppv2-text-sm poppinsMedium ppv2-text-[#c0392b]">
                        ✕ {{ testErrorMessage || $t('configuration.integration.testEmailError') }}
                    </span>
                </div>
            </div>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import GoogleCalendarIntegration from './GoogleCalendarIntegration.vue'
import McpIntegration from './McpIntegration.vue'
import axios from 'axios'
import normalizeSize from '../../../../utils/normalizeSize'

export default {
    name: 'IntegrationSettingsModal',
    components: {
        CustomDialog,
        CustomInput,
        BaseTextarea,
        BaseButton,
        GoogleCalendarIntegration,
        McpIntegration,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        integration: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            isSendingTest: false,
            form: {
                settings: [],
            },
            errors: {},
            testStatus: 'idle',
            testErrorMessage: '',
            loadedSettings: {},
        }
    },
    computed: {
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        canSendTest() {
            if (!this.integration || this.integration.id !== 'mailjet') return false
            const keys = this.form.settings.filter((s) => ['mailjet_api_key', 'mailjet_secret_key'].includes(s.key))
            return keys.length === 2 && keys.every((s) => s.value && s.value.trim() !== '')
        },
        isMailjet() {
            return this.integration && this.integration.id === 'mailjet'
        },
        isMcp() {
            // Rien à « enregistrer » : le panneau MCP agit par ses propres boutons.
            return this.integration && this.integration.id === 'mcp'
        },
        hasSavedApiKey() {
            // Source de vérité serveur : `is_masked` est renvoyé par fetch() quand la
            // clé était déjà enregistrée (valeur masquée). On ne ré-infère jamais le
            // masquage par un caractère magique côté Vue. La clé n'est « enregistrée »
            // que si l'utilisateur n'a pas modifié la valeur depuis l'ouverture :
            // dès qu'il remplace une clé masquée, on repasse en mode « Tester et
            // enregistrer » (un seul bouton) comme en premier paramétrage.
            return this.form.settings.some((s) => s.is_masked && s.value === this.loadedSettings[s.key])
        },
    },
    watch: {
        integration: {
            handler(newVal) {
                if (newVal && newVal.settings) {
                    this.form.settings = JSON.parse(JSON.stringify(newVal.settings))
                    this.loadedSettings = this.form.settings.reduce((acc, s) => {
                        acc[s.key] = s.value
                        return acc
                    }, {})
                } else {
                    this.form.settings = []
                    this.loadedSettings = {}
                }
                this.errors = {}
                this.testStatus = 'idle'
                this.testErrorMessage = ''
            },
            immediate: true,
        },
    },
    methods: {
        normalizeSize,
        async handleSubmit() {
            this.errors = {}
            let hasError = false

            // Simple validation for required fields (e.g., script for tools)
            // Removed mandatory script validation as requested

            if (hasError) {
                return
            }

            // For Google Calendar, call the saveRoles method via ref
            if (this.integration && this.integration.id === 'google_calendar') {
                if (this.$refs.googleCalendarRef) {
                    await this.$refs.googleCalendarRef.saveRoles()
                }
            } else {
                // For other integrations, use the standard submit
                await this.submit()
            }
        },
        async submit() {
            this.isLoading = true
            try {
                const response = await axios.post('/back-office/v3/integration/update-settings', {
                    id: this.integration.id,
                    settings: this.form.settings,
                })
                if (response.data.success) {
                    this.$toast.success(response.data.message)
                    this.$emit('success')
                    this.isVisible = false
                    return true
                }
                this.testStatus = 'idle'
                this.$toast.error(response.data.message)
                return false
            } catch (error) {
                this.testStatus = 'idle'
                this.$toast.error(error.response?.data?.message || this.$t('common.save_error'))
                return false
            } finally {
                this.isLoading = false
            }
        },
        onSuccess(message) {
            this.$toast.success(message)
            this.$emit('success')
            this.isVisible = false
        },
        onError(message) {
            this.$toast.error(message)
        },
        async handleTestAndSave() {
            if (!this.canSendTest) return
            await this.sendTestEmail()
            if (this.testStatus === 'success') {
                // Le save peut échouer (réseau, 5xx) alors que le test est passé :
                // repasser le statut à l'état neutre pour que le « ✓ » ne persiste pas.
                await this.submit()
            }
        },
        async sendTestEmail() {
            this.isSendingTest = true
            this.testStatus = 'testing'
            this.testErrorMessage = ''
            try {
                const apiKeyObj = this.form.settings.find((s) => s.key === 'mailjet_api_key')
                const secretKeyObj = this.form.settings.find((s) => s.key === 'mailjet_secret_key')
                const payload = {
                    mailjet_api_key: apiKeyObj ? apiKeyObj.value : null,
                    mailjet_secret_key: secretKeyObj ? secretKeyObj.value : null,
                }
                const response = await axios.post('/back-office/v3/integration/send-test-email', payload)
                if (response.data.success) {
                    this.testStatus = 'success'
                    this.$toast.success(response.data.message)
                } else {
                    this.testStatus = 'error'
                    this.testErrorMessage = response.data.message
                }
            } catch (error) {
                this.testStatus = 'error'
                this.testErrorMessage = error.response?.data?.message || this.$t('configuration.integration.testEmailError')
                this.$toast.error(this.testErrorMessage)
            } finally {
                this.isSendingTest = false
            }
        },
    },
}
</script>

<style scoped>
.mcp-panel-scroll {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 4px;
}
/* Add any specific styles to match the image if needed */
:deep(.ppv2-dialog-title) {
    font-size: 24px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
}
</style>
